/**
 * Stokastic Props Tool
 *
 * IMPORTANT: This tool uses the omaha-design-system library.
 * omaha-design-system is a MUI wrapper and should ONLY be used in this tool
 * to avoid conflicts with the existing @material-ui and @mui dependencies
 * used elsewhere in the application.
 *
 * DO NOT import from omaha-design-system in any other tool.
 */
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ThemeProvider, darkTheme, Stack } from 'omaha-design-system';
import ToolsHeader from '../common/ToolsHeader';
import { BANNER_HEIGHT_CSS } from '../common/BannerManager';
import MobileAppHeader from '../common/MobileAppHeader';
import { Helmet } from 'react-helmet';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Authentication from '../common/Authentication';
import PropsDesktop from './components/PropsDesktop';
import { setLeagueFilter } from './store/slice';
import { DEFAULT_PROPS_LEAGUE, isPropsLeague } from './data/leagueConfig';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

// Inner component that syncs URL league to Redux state
const PropsContent: React.FC = () => {
  const { league } = useParams<{ league: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  // Sync URL league to Redux state; a league the tool doesn't support falls back to NBA.
  useEffect(() => {
    if (!league) return;
    const upper = league.toUpperCase();
    if (!isPropsLeague(upper)) {
      history.replace(`/props/${DEFAULT_PROPS_LEAGUE}`);
      return;
    }
    dispatch(setLeagueFilter(upper));
  }, [league, dispatch, history]);

  return (
    <Stack
      sx={{
        fontFamily: 'Inter',
        backgroundColor: '#0D0E10',
        flexGrow: 1,
        // Subtract the app-level promo banner so this fixed-height layout
        // doesn't push its bottom content off-screen while a banner is showing.
        height: `calc(100vh - ${BANNER_HEIGHT_CSS})`,
        overflow: 'hidden',
        overscrollBehavior: 'none', // Prevent pull-to-refresh on mobile
        // Global scrollbar styling
        '& *::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '& *::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '& *::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          borderRadius: '4px',
        },
        '& *::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.35)',
        },
        '& *': {
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.15) transparent',
        },
      }}
    >
      <Helmet>
        <title>Stokastic Props - {league?.toUpperCase() || DEFAULT_PROPS_LEAGUE}</title>
        <meta name="description" content={`${league?.toUpperCase() || DEFAULT_PROPS_LEAGUE} Props Betting Tool - Find +EV player props`} />
      </Helmet>
      <ToolsHeader />
      <MobileAppHeader title="Props" />
      <PropsDesktop />
    </Stack>
  );
};

const StokasticProps = () => {
  return (
    <ThemeProvider theme={darkTheme}>
      <QueryClientProvider client={queryClient}>
        <Authentication app="PROPS">
          <PropsContent />
        </Authentication>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default StokasticProps;
